import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconRefresh } from '@tabler/icons-react';
import { Button } from '@/admin/components/ui/button';
import {
  type ActiveFilterChip,
  type FilterBuilderOption,
} from '@/admin/components/common/filter-builder';
import {
  fetchAdminChatFacets,
  fetchAdminConversations,
  type AdminChat,
  type AdminChatFacetsResponse,
  type AdminChatSearchFilters,
} from '@/admin/features/chat-analytics/api';
import { ChatFilters } from '../components/chat-list/chat-filters';
import { ChatTable } from '../components/chat-list/chat-table';

const PAGE_SIZE = 25;
const HIDDEN_VALUES = ['true', 'false'] as const;
const DEFAULT_VISIBILITY_FILTER = 'false';
const FILTER_KEYS = [
  'user_id',
  'session_id',
  'chat_id',
  'conversation_id',
  'agent_id',
  'tenant_id',
  'status',
  'purpose',
  'hidden',
  'q',
  'start_date',
  'end_date',
  'cursor',
] as const;

type FilterKey = (typeof FILTER_KEYS)[number];
type DraftFilters = Record<FilterKey, string>;

const emptyDraftFilters = (): DraftFilters => ({
  user_id: '',
  session_id: '',
  chat_id: '',
  conversation_id: '',
  agent_id: '',
  tenant_id: '',
  status: '',
  purpose: '',
  hidden: '',
  q: '',
  start_date: '',
  end_date: '',
  cursor: '',
});

function draftFromSearchParams(
  searchParams: URLSearchParams,
  options?: { defaultHidden?: string }
): DraftFilters {
  const draft = emptyDraftFilters();
  for (const key of FILTER_KEYS) {
    draft[key] = searchParams.get(key) || draft[key];
  }
  if (!draft.hidden && options?.defaultHidden) {
    draft.hidden = options.defaultHidden;
  }
  return draft;
}

function filtersFromDraft(draft: DraftFilters): AdminChatSearchFilters {
  const hidden = draft.hidden.trim();
  return {
    tenant_id: draft.tenant_id.trim() || undefined,
    user_id: draft.user_id.trim() || undefined,
    agent_id: draft.agent_id.trim() || undefined,
    session_id: draft.session_id.trim() || undefined,
    chat_id: draft.chat_id.trim() || undefined,
    conversation_id: draft.conversation_id.trim() || undefined,
    status: draft.status.trim() || undefined,
    purpose: draft.purpose.trim() || undefined,
    hidden: HIDDEN_VALUES.includes(hidden as (typeof HIDDEN_VALUES)[number])
      ? hidden === 'true'
      : undefined,
    q: draft.q.trim() || undefined,
    start_date: draft.start_date.trim() || undefined,
    end_date: draft.end_date.trim() || undefined,
    cursor: draft.cursor.trim() || undefined,
    limit: PAGE_SIZE,
  };
}

function updateSearchParams(
  setSearchParams: ReturnType<typeof useSearchParams>[1],
  draft: DraftFilters,
  options?: { clearCursor?: boolean }
) {
  const next = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    if (options?.clearCursor && key === 'cursor') {
      continue;
    }
    const value = draft[key].trim();
    if (value) {
      next.set(key, value);
    }
  }
  setSearchParams(next);
}

export function ChatsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const appliedDefaultVisibilityRef = React.useRef(false);
  const defaultVisibilityPendingRef = React.useRef(!searchParams.has('hidden'));
  const [draft, setDraft] = React.useState(() =>
    draftFromSearchParams(searchParams, {
      defaultHidden: defaultVisibilityPendingRef.current
        ? DEFAULT_VISIBILITY_FILTER
        : undefined,
    })
  );
  const [chats, setChats] = React.useState<AdminChat[]>([]);
  const [nextCursor, setNextCursor] = React.useState('');
  const [cursorHistory, setCursorHistory] = React.useState<string[]>([]);
  const [facets, setFacets] = React.useState<AdminChatFacetsResponse | null>(
    null
  );
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!defaultVisibilityPendingRef.current) return;
    defaultVisibilityPendingRef.current = false;
    if (appliedDefaultVisibilityRef.current) return;
    appliedDefaultVisibilityRef.current = true;
    if (searchParams.has('hidden')) return;
    const next = new URLSearchParams(searchParams);
    next.set('hidden', DEFAULT_VISIBILITY_FILTER);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  React.useEffect(() => {
    setDraft(draftFromSearchParams(searchParams));
  }, [searchParams]);

  React.useEffect(() => {
    let canceled = false;
    fetchAdminChatFacets()
      .then(response => {
        if (!canceled) setFacets(response);
      })
      .catch(() => {
        if (!canceled) setFacets(null);
      });
    return () => {
      canceled = true;
    };
  }, []);

  const queryDraft = React.useMemo(
    () =>
      draftFromSearchParams(searchParams, {
        defaultHidden: defaultVisibilityPendingRef.current
          ? DEFAULT_VISIBILITY_FILTER
          : undefined,
      }),
    [searchParams]
  );

  const loadChats = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAdminConversations(
        filtersFromDraft(queryDraft)
      );
      setChats(response.conversations || []);
      setNextCursor(response.next_cursor || '');
    } catch (err) {
      setChats([]);
      setNextCursor('');
      setError(err instanceof Error ? err.message : 'Failed to load chats');
    } finally {
      setLoading(false);
    }
  }, [queryDraft]);

  React.useEffect(() => {
    loadChats();
  }, [loadChats]);

  const filterOptions = React.useMemo<FilterBuilderOption[]>(
    () => [
      { key: 'user_id', label: 'User ID', placeholder: 'user-local' },
      { key: 'session_id', label: 'Session ID', placeholder: 'session-errors' },
      { key: 'chat_id', label: 'Chat ID' },
      { key: 'conversation_id', label: 'Conversation ID' },
      { key: 'agent_id', label: 'Agent ID' },
      { key: 'tenant_id', label: 'Tenant ID' },
      {
        key: 'q',
        label: 'Search text',
        placeholder: 'title, message, or id text',
      },
      {
        key: 'status',
        label: 'Session status',
        type: 'select',
        values: (facets?.statuses || []).map(status => ({
          value: status,
          label: status,
        })),
      },
      {
        key: 'purpose',
        label: 'Session purpose',
        type: 'select',
        values: (facets?.purposes || []).map(purpose => ({
          value: purpose,
          label: purpose,
        })),
      },
      {
        key: 'hidden',
        label: 'Visibility',
        type: 'select',
        values: [
          { value: 'false', label: 'Visible' },
          { value: 'true', label: 'Hidden' },
        ],
      },
      { key: 'start_date', label: 'Start date', type: 'date' },
      { key: 'end_date', label: 'End date', type: 'date' },
    ],
    [facets?.purposes, facets?.statuses]
  );

  const activeFilterChips = React.useMemo<ActiveFilterChip[]>(() => {
    const labels = new Map(filterOptions.map(option => [option.key, option]));
    return FILTER_KEYS.filter(key => key !== 'cursor')
      .map(key => {
        const value = draft[key].trim();
        const option = labels.get(key);
        if (!value || !option) return null;
        return {
          key,
          label: option.label,
          value,
          valueLabel: option.values?.find(item => item.value === value)?.label,
        };
      })
      .filter(Boolean) as ActiveFilterChip[];
  }, [draft, filterOptions]);

  const addFilter = (key: string, value: string) => {
    if (!FILTER_KEYS.includes(key as FilterKey)) return;
    const next = { ...draft, [key]: value, cursor: '' } as DraftFilters;
    setDraft(next);
    setCursorHistory([]);
    updateSearchParams(setSearchParams, next, { clearCursor: true });
  };

  const removeFilter = (key: string) => {
    if (!FILTER_KEYS.includes(key as FilterKey)) return;
    const next = { ...draft, [key]: '', cursor: '' } as DraftFilters;
    setDraft(next);
    setCursorHistory([]);
    updateSearchParams(setSearchParams, next, { clearCursor: true });
  };

  const handleClear = () => {
    setCursorHistory([]);
    const next = emptyDraftFilters();
    setDraft(next);
    updateSearchParams(setSearchParams, next, { clearCursor: true });
  };

  const handleNextPage = () => {
    if (!nextCursor) return;
    setCursorHistory(history => [...history, queryDraft.cursor]);
    updateSearchParams(setSearchParams, { ...queryDraft, cursor: nextCursor });
  };

  const handlePreviousPage = () => {
    setCursorHistory(history => {
      const next = history.slice(0, -1);
      updateSearchParams(setSearchParams, {
        ...queryDraft,
        cursor: history[history.length - 1] || '',
      });
      return next;
    });
  };

  const openDetail = (chat: AdminChat) => {
    const params = new URLSearchParams();
    params.set('tenant_id', chat.tenant_id);
    params.set('agent_id', chat.agent_id);
    if (chat.agent_group_id) params.set('agent_group_id', chat.agent_group_id);
    if (chat.user_id) params.set('user_id', chat.user_id);
    if (chat.conversation_id) {
      navigate(
        `/conversations/${encodeURIComponent(chat.conversation_id)}?${params}`
      );
      return;
    }
    navigate(`/chats/${encodeURIComponent(chat.session_id)}?${params}`);
  };

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-auto py-4 lg:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-normal">
              Conversations
            </h1>
            <p className="text-sm text-muted-foreground">
              Search user conversations and open runtime sessions when needed.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadChats}
            disabled={loading}
          >
            <IconRefresh className="size-4" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="px-4 lg:px-6">
        <ChatFilters
          options={filterOptions}
          activeFilters={activeFilterChips}
          onAdd={addFilter}
          onRemove={removeFilter}
          onClear={handleClear}
        />
      </div>

      <div className="px-4 lg:px-6">
        <ChatTable
          chats={chats}
          loading={loading}
          error={error}
          nextCursor={nextCursor}
          canGoPrevious={cursorHistory.length > 0}
          onOpenDetail={openDetail}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
        />
      </div>
    </div>
  );
}
